import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  History,
  Filter,
  User,
  QrCode,
  Folder,
  MessageSquare,
  UserPlus,
  UserMinus,
  CheckCircle,
  XCircle,
  Edit,
  Trash,
  Plus,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useActivityLogs, ACTION_LABELS, ENTITY_LABELS } from '@/hooks/useActivityLogs';

interface ActivityLogProps {
  workspaceId: string;
}

const actionIcons: Record<string, typeof Plus> = {
  created: Plus,
  updated: Edit,
  deleted: Trash,
  approved: CheckCircle,
  rejected: XCircle,
  invited: UserPlus,
  joined: UserPlus,
  left: UserMinus,
  commented: MessageSquare,
};

const entityIcons: Record<string, typeof QrCode> = {
  qr_code: QrCode,
  folder: Folder,
  member: User,
  comment: MessageSquare,
};

export function ActivityLog({ workspaceId }: ActivityLogProps) {
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>('all');
  const [actionFilter, setActionFilter] = useState<string>('all');

  const { data: logs, isLoading } = useActivityLogs(workspaceId, {
    entityType: entityTypeFilter !== 'all' ? entityTypeFilter : undefined,
    action: actionFilter !== 'all' ? actionFilter : undefined,
    limit: 100,
  });

  const ActionIcon = (action: string) => actionIcons[action] || Edit;
  const EntityIcon = (entity: string) => entityIcons[entity] || QrCode;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Activity Log
            </CardTitle>
            <CardDescription>
              Track all actions in this workspace
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={entityTypeFilter} onValueChange={setEntityTypeFilter}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="qr_code">QR Codes</SelectItem>
                <SelectItem value="folder">Folders</SelectItem>
                <SelectItem value="member">Members</SelectItem>
                <SelectItem value="comment">Comments</SelectItem>
              </SelectContent>
            </Select>
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="All actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All actions</SelectItem>
                <SelectItem value="created">Created</SelectItem>
                <SelectItem value="updated">Updated</SelectItem>
                <SelectItem value="deleted">Deleted</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[400px] pr-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : logs && logs.length > 0 ? (
            <div className="space-y-4">
              {logs.map((log) => {
                const Icon = ActionIcon(log.action);
                const EIcon = EntityIcon(log.entityType);
                
                return (
                  <div key={log.id} className="flex items-start gap-3 p-3 rounded-lg border">
                    <div className="p-2 rounded-full bg-muted">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">
                          {ACTION_LABELS[log.action] || log.action}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          <EIcon className="h-3 w-3 mr-1" />
                          {ENTITY_LABELS[log.entityType] || log.entityType}
                        </Badge>
                        {log.entityName && (
                          <span className="text-sm text-muted-foreground truncate">
                            "{log.entityName}"
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDistanceToNow(new Date(log.createdAt), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
              <History className="h-8 w-8 mb-2 opacity-50" />
              <p className="text-sm">No activity yet</p>
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
